import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { del, errorMessage, get, post } from "../../lib/api";
import { centsToUsd, creditsToUsd, formatDate, formatNumber } from "../../lib/format";
import { usePublicConfig, useRefreshMe } from "../../lib/session";
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Loading, Modal, PageHeader, Select, Stat, Table, Td } from "../../components/ui";
import { usePricing } from "../workspace/BillingPage";

interface PartnerData {
  partner: { id: string; name: string; logo_url: string | null; created_at: number };
  role: "partner_admin" | "partner_operator";
  wholesaleDiscountPct: number;
  balance: number;
  workspaces: Array<{ id: string; name: string; region: string; resale_markup_pct: number | null; created_at: number; balance: number }>;
}

const usePartnerId = () => (useParams({ strict: false }) as { partnerId: string }).partnerId;

function usePartner(id: string) {
  return useQuery({ queryKey: ["partner", id], queryFn: () => get<PartnerData>(`/api/partners/${id}`) });
}

export function PartnerHomePage() {
  const id = usePartnerId();
  const qc = useQueryClient();
  const refreshMe = useRefreshMe();
  const cfg = usePublicConfig();
  const p = usePartner(id);
  const pricing = usePricing();
  const [creating, setCreating] = useState(false);
  const [allocTo, setAllocTo] = useState<string | null>(null);
  const [packFor, setPackFor] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  if (p.isLoading) return <Loading />;
  const d = p.data!;
  const admin = d.role === "partner_admin";
  const refresh = () => qc.invalidateQueries({ queryKey: ["partner", id] });

  const buyCredits = async (itemId: string) => {
    try {
      const r = await post<{ url: string }>(`/api/billing/partners/${id}/checkout`, { kind: "credits", itemId });
      window.location.assign(r.url);
    } catch (e) {
      setMsg({ tone: "danger", text: errorMessage(e) });
    }
  };

  return (
    <>
      <PageHeader title={d.partner.name} subtitle={`Partner account · wholesale discount ${d.wholesaleDiscountPct}% · you're ${d.role.replace("_", " ")}`} actions={admin && <Button onClick={() => setCreating(true)}>New client workspace</Button>} />
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Stat label="Partner credits" value={formatNumber(d.balance)} hint={`${creditsToUsd(d.balance)} at list price`} />
        <Stat label="Client workspaces" value={d.workspaces.length} />
        <Stat label="Credits held by clients" value={formatNumber(d.workspaces.reduce((s, w) => s + w.balance, 0))} />
      </div>

      <Card title="Clients" className="mb-6">
        {d.workspaces.length ? (
          <Table head={["Client", "Region", "Credits", "Your markup", ""]}>
            {d.workspaces.map((w) => (
              <tr key={w.id}>
                <Td>
                  <Link to="/w/$workspaceId" params={{ workspaceId: w.id }} className="text-accent font-medium hover:underline">
                    {w.name}
                  </Link>
                </Td>
                <Td>{w.region.toUpperCase()}</Td>
                <Td className="tabular">{formatNumber(w.balance)}</Td>
                <Td>{w.resale_markup_pct !== null ? `${w.resale_markup_pct}%` : "—"}</Td>
                <Td className="text-right whitespace-nowrap">
                  {admin && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setAllocTo(w.id)}>
                        Allocate credits
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setPackFor(w.id)}>
                        Buy pack
                      </Button>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        ) : (
          <EmptyState title="No clients yet" action={admin && <Button onClick={() => setCreating(true)}>New client workspace</Button>}>
            Create a workspace for each client. Their data stays isolated, and you can allocate credits at your wholesale price.
          </EmptyState>
        )}
      </Card>

      {admin && pricing.data && (
        <Card title="Buy partner credits" actions={(cfg.data?.environment === "dev" || cfg.data?.environment === "test") && <Button size="sm" variant="secondary" onClick={async () => { await post("/api/dev/billing/credits", { walletId: `partner:${id}`, credits: 50_000 }); refresh(); }}>Add 50,000 test credits</Button>}>
          <p className="muted mb-3 text-sm">You pay wholesale ({d.wholesaleDiscountPct}% off list), allocate to clients, and bill them however you like.</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {pricing.data.bundles.map((b) => (
              <div key={b.id} className="border-default rounded-lg border p-3">
                <div className="font-medium">{formatNumber(b.credits)} credits</div>
                <div className="muted text-xs">
                  {centsToUsd(Math.round(b.priceCents * (1 - d.wholesaleDiscountPct / 100)))} <s>{centsToUsd(b.priceCents)}</s>
                </div>
                <Button size="sm" className="mt-2" onClick={() => buyCredits(b.id)}>
                  Buy
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {creating && (
        <CreateClientModal
          partnerId={id}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            refresh();
            refreshMe();
          }}
        />
      )}
      {allocTo && (
        <AllocateModal
          partnerId={id}
          workspace={d.workspaces.find((w) => w.id === allocTo)!}
          max={d.balance}
          onClose={() => setAllocTo(null)}
          onDone={(n) => {
            setAllocTo(null);
            setMsg({ tone: "ok", text: `Allocated ${formatNumber(n)} credits.` });
            refresh();
          }}
        />
      )}
      {packFor && pricing.data && (
        <Modal open onClose={() => setPackFor(null)} title="Buy a migration pack for this client">
          <div className="space-y-2">
            {pricing.data.packs.map((pk) => (
              <div key={pk.id} className="border-default flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="font-medium">{pk.name}</div>
                  <div className="muted text-xs">{centsToUsd(Math.round(pk.priceCents * (1 - d.wholesaleDiscountPct / 100)))} wholesale</div>
                </div>
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      const r = await post<{ url: string }>(`/api/billing/partners/${id}/workspaces/${packFor}/checkout`, { kind: "pack", itemId: pk.id });
                      window.location.assign(r.url);
                    } catch (e) {
                      setPackFor(null);
                      setMsg({ tone: "danger", text: errorMessage(e) });
                    }
                  }}
                >
                  Buy
                </Button>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}

function CreateClientModal({ partnerId, onClose, onDone }: { partnerId: string; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState("");
  const [markup, setMarkup] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      open
      onClose={onClose}
      title="New client workspace"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!name}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await post(`/api/partners/${partnerId}/workspaces`, { name, resaleMarkupPct: markup === "" ? null : Number(markup) });
                onDone();
              } catch (e) {
                setError(errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Create
          </Button>
        </>
      }
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Client name" htmlFor="cn">
        <Input id="cn" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Your markup on list price (%)" htmlFor="mk" hint="Optional. Used for your margin report; you bill the client yourself.">
        <Input id="mk" type="number" min={0} max={1000} value={markup} onChange={(e) => setMarkup(e.target.value)} />
      </Field>
    </Modal>
  );
}

function AllocateModal({ partnerId, workspace, max, onClose, onDone }: { partnerId: string; workspace: { id: string; name: string }; max: number; onClose: () => void; onDone: (n: number) => void }) {
  const [credits, setCredits] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const n = Number(credits);
  return (
    <Modal
      open
      onClose={onClose}
      title={`Allocate credits to ${workspace.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!n || n > max}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await post(`/api/partners/${partnerId}/allocations`, { workspaceId: workspace.id, credits: n });
                onDone(n);
              } catch (e) {
                setError(errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Allocate
          </Button>
        </>
      }
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Credits" htmlFor="cr" hint={`Available: ${formatNumber(max)}`}>
        <Input id="cr" type="number" min={1} max={max} value={credits} onChange={(e) => setCredits(e.target.value)} />
      </Field>
    </Modal>
  );
}

export function PartnerReportPage() {
  const id = usePartnerId();
  const now = new Date();
  const [from, setFrom] = useState(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date(Date.now() + 86_400_000).toISOString().slice(0, 10));
  const range = `from=${Date.parse(from)}&to=${Date.parse(to)}`;
  const report = useQuery({
    queryKey: ["partner-report", id, range],
    queryFn: () => get<{ wholesaleDiscountPct: number; rows: Array<{ workspaceId: string; client: string; creditsUsed: number; storageCredits: number; yourCostUsd: string; resaleUsd: string; marginUsd: string; markupPct: number }> }>(`/api/partners/${id}/report?${range}`),
  });
  const total = (k: "yourCostUsd" | "resaleUsd" | "marginUsd") => (report.data?.rows ?? []).reduce((s, r) => s + Number(r[k]), 0).toFixed(2);
  return (
    <>
      <PageHeader title="Usage & margin" subtitle="Per-client usage at your wholesale cost and your resale price. Use it to invoice your clients." actions={<a href={`/api/partners/${id}/report?${range}&format=csv`}><Button variant="secondary">Download CSV</Button></a>} />
      <Card className="mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="From" htmlFor="from">
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To" htmlFor="to">
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </Card>
      {report.isLoading ? (
        <Loading />
      ) : (
        <Card>
          <Table head={["Client", "Credits used", "Your cost", "Resale", "Margin", "Markup"]}>
            {report.data!.rows.map((r) => (
              <tr key={r.workspaceId}>
                <Td className="font-medium">{r.client}</Td>
                <Td className="tabular">{formatNumber(r.creditsUsed)}</Td>
                <Td className="tabular">${r.yourCostUsd}</Td>
                <Td className="tabular">${r.resaleUsd}</Td>
                <Td className="tabular text-ok">${r.marginUsd}</Td>
                <Td>{r.markupPct}%</Td>
              </tr>
            ))}
            <tr className="font-semibold">
              <Td>Total</Td>
              <Td> </Td>
              <Td className="tabular">${total("yourCostUsd")}</Td>
              <Td className="tabular">${total("resaleUsd")}</Td>
              <Td className="tabular">${total("marginUsd")}</Td>
              <Td> </Td>
            </tr>
          </Table>
        </Card>
      )}
    </>
  );
}

export function PartnerTeamPage() {
  const id = usePartnerId();
  const qc = useQueryClient();
  const p = usePartner(id);
  const members = useQuery({ queryKey: ["partner-members", id], queryFn: () => get<{ members: Array<{ id: string; name: string; email: string; role: string }> }>(`/api/partners/${id}/members`) });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("partner_operator");
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const admin = p.data?.role === "partner_admin";
  if (members.isLoading) return <Loading />;
  return (
    <>
      <PageHeader title="Team" subtitle="People in your agency. They get access to every client workspace of this partner account." />
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      {admin && (
        <Card title="Invite a teammate" className="mb-6">
          <form
            className="grid gap-3 sm:grid-cols-[1fr_200px_auto]"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await post(`/api/partners/${id}/invites`, { email, role });
                setMsg({ tone: "ok", text: `Invite sent to ${email}.` });
                setEmail("");
              } catch (err) {
                setMsg({ tone: "danger", text: errorMessage(err) });
              }
            }}
          >
            <Input type="email" required aria-label="Email" placeholder="name@agency.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="partner_operator">Partner operator</option>
              <option value="partner_admin">Partner admin</option>
            </Select>
            <Button type="submit">Invite</Button>
          </form>
        </Card>
      )}
      <Card title="Members">
        <Table head={["Name", "Email", "Role", ""]}>
          {members.data!.members.map((m) => (
            <tr key={m.id}>
              <Td>{m.name}</Td>
              <Td className="muted">{m.email}</Td>
              <Td>
                <Badge>{m.role.replace("_", " ")}</Badge>
              </Td>
              <Td className="text-right">
                {admin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      if (!confirm(`Remove ${m.email}?`)) return;
                      try {
                        await del(`/api/partners/${id}/members/${m.id}`);
                        qc.invalidateQueries({ queryKey: ["partner-members", id] });
                      } catch (err) {
                        setMsg({ tone: "danger", text: errorMessage(err) });
                      }
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
        <p className="muted mt-3 text-xs">Created {formatDate(p.data?.partner.created_at)}</p>
      </Card>
    </>
  );
}
